import { Button, Flex, Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
/* eslint-disable max-statements */
import { cloneDeep, get } from 'lodash';
import { useEffect, useState } from 'react';

import { DeclarationDocumentsEnum, declarationDocumentsIdsMapping, Documents } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { DeclarationDocumentView, DocumentChangeProps, Table, TableActionEnum, TableActions, TableColumnProps } from '@components';
import { MutateDeclarationDocumentOperation } from '@hooks/declaration';
import { retrieveCodes } from '@hooks/retrieveCodes';
import { useCodeListSystem } from '@hooks/useCodeListSystem';

interface DocumentItem {
  type: DeclarationDocumentsEnum;
  label: string;
  dataSource: any;
  guaranteeType: string;
  guaranteeTypeDescription: string;
  grn?: string;
  currencyCode?: string;
  amountTobeCovered?: number;
  otherGuaranteeReference?: string;
  customsOfficeGuarantee?: string;
}

interface DocDetailsProps {
  type: DeclarationDocumentsEnum;
  dataSource: any;
  dataSourceIndex?: number;
}

interface GuaranteesDocumentListProps {
  onDocumentChange(document: DocumentChangeProps): void;
  documents: Documents;
  language: string;
}

export const GuaranteesDocumentList: React.FC<GuaranteesDocumentListProps> = ({ documents, onDocumentChange, language }) => {
  const codelistSystems = useCodeListSystem({ group: 'H1' });
  const [guaranteeTypes, setGuaranteeTypes] = useState<{ value?: string; label?: string }[]>([]);

  const [data, setData] = useState<DocumentItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDeleteDocument, setSelectedDeleteDocument] = useState<DocumentItem>();
  const [selectedDocument, setSelectedDocument] = useState<DocDetailsProps>(undefined);

  const toggleShowDocumentModal = (inEdit?: boolean) => {
    if (!inEdit) {
      setSelectedDocument(undefined);
    }
    setShowModal(!showModal);
  };

  useEffect(() => {
    retrieveCodes('CL251', '', language, codelistSystems).then((response) => {
      setGuaranteeTypes(response);
    });
  }, [codelistSystems]);

  useEffect(() => {
    const tableData: DocumentItem[] = [];
    const documentType = DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS;

    documents[documentType]?.forEach((item) =>
      tableData.push({
        type: documentType as DeclarationDocumentsEnum,
        label: 'Guarantee document',
        dataSource: item,
        guaranteeType: item.type,
        guaranteeTypeDescription: guaranteeTypes.find((type) => type.value === item.type)?.label ?? '',
        grn: item.reference?.grn,
        currencyCode: item.reference?.currencyCode,
        amountTobeCovered: item.reference?.amountTobeCovered,
        otherGuaranteeReference: item.reference?.otherGuaranteeReference,
        customsOfficeGuarantee: item.reference?.customsOfficeGuarantee?.referenceNumber,
      }),
    );

    setData(tableData);
  }, [documents, guaranteeTypes]);

  const handleSavingDocument = async (documentData: { id: Record<string, any>; type: DeclarationDocumentsEnum; data: any }) => {
    const updatedDoc = cloneDeep(documentData.data);
    const { type, id } = documentData;

    onDocumentChange({
      operation: selectedDocument ? MutateDeclarationDocumentOperation.UPDATE : MutateDeclarationDocumentOperation.CREATE,
      type,
      id,
      data: updatedDoc,
    });
    setSelectedDocument(undefined);
  };

  const handleTableAction = (type: string, row: DocumentItem) => {
    switch (type) {
      case TableActionEnum.Delete:
        setSelectedDeleteDocument(row);
        break;
      default:
        setSelectedDocument({
          type: row.type,
          dataSource: row.dataSource,
        });
        toggleShowDocumentModal(true);
    }
  };

  const columns: TableColumnProps<DocumentItem>[] = [
    {
      name: 'Guarantee type',
      cell: (row, rowIdx) => (
        <Tooltip label={row.guaranteeTypeDescription} color="blue" multiline w={'fit-content'} maw={320} p={16} position="top-start" offset={5}>
          <span data-pw={`cell-guarantee-type-${rowIdx}`}>{row.guaranteeType}</span>
        </Tooltip>
      ),
    },
    {
      name: 'GNR',
      cell: (row, rowIdx) => <span data-pw={`cell-grn-${rowIdx}`}>{row.grn}</span>,
    },
    {
      name: 'Currency',
      cell: (row, rowIdx) => <span data-pw={`cell-currency-${rowIdx}`}>{row.currencyCode}</span>,
    },
    {
      name: 'Amount to be covered',
      cell: (row, rowIdx) => <span data-pw={`cell-amount-to-be-covered-${rowIdx}`}>{row.amountTobeCovered}</span>,
    },
    {
      name: 'Other guarantee reference',
      cell: (row, rowIdx) => <span data-pw={`cell-other-guarantee-reference-${rowIdx}`}>{row.otherGuaranteeReference}</span>,
    },
    {
      name: 'Guarantee office',
      cell: (row, rowIdx) => <span data-pw={`cell-guarantee-office-${rowIdx}`}>{row.customsOfficeGuarantee}</span>,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: DocumentItem, rowIdx: number) => (
        <TableActions
          data-pw={rowIdx}
          actions={[
            {
              icon: IconFactory.view,
              text: 'View details',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            },
          ]}
        />
      ),
    },
  ];

  const handleDocumentDelete = (row: DocumentItem) => {
    onDocumentChange({
      operation: MutateDeclarationDocumentOperation.DELETE,
      type: row.type,
      id: declarationDocumentsIdsMapping[row.type].reduce((idsList, id) => {
        return { ...idsList, [id]: get(row.dataSource, id) };
      }, {}),
      data: row.dataSource,
    });
    setSelectedDeleteDocument(undefined);
  };

  return (
    <Stack style={{ flexShrink: 0 }}>
      <Flex justify={'end'} py={12}>
        <Button onClick={() => toggleShowDocumentModal()} variant="outline" size="lg">
          Add new
        </Button>
      </Flex>
      <Table columns={columns} dataSource={data || []} pagination verticalSpacing="xs" />
      {selectedDeleteDocument && (
        <Modal title="Delete document" opened={true} onClose={() => setSelectedDeleteDocument(undefined)} centered zIndex={9999}>
          <Text>Are you sure you want to delete this document?</Text>

          <Group justify="center" mt="xl">
            <Button variant="outline" onClick={() => setSelectedDeleteDocument(undefined)}>
              Cancel
            </Button>
            <Button data-pw="delete-document-btn" variant="filled" color="red" onClick={() => handleDocumentDelete(selectedDeleteDocument)}>
              Delete
            </Button>
          </Group>
        </Modal>
      )}
      {showModal && (
        <DeclarationDocumentView
          opened={true}
          onHide={toggleShowDocumentModal}
          docDetails={selectedDocument}
          includeDocumentTypes={[DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS]}
          excludeDocumentTypes={null}
          onSave={handleSavingDocument}
          editDisabled={false}
          isHighValue={true}
        />
      )}
    </Stack>
  );
};
